/**
 * Google Ads OAuth connection card (Path B — P0.7 minimal viable UI).
 *
 * Ported from agileco-planner's google-ads-connection-card.tsx, adapted
 * to Ant Design + the seo project's apiClient pattern. Sits at the top
 * of the workspace-settings → integrations tab as the recommended path,
 * leaving the legacy manual-entry fields in place for clients NOT under
 * the agency MCC.
 */
import { useEffect, useState } from 'react';
import { COPY } from '@/lib/copyTokens';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Divider,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
  Tooltip,
} from 'antd';
import {
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  XCircle,
  Link as LinkIcon,
} from 'lucide-react';
import {
  AGENCY_TENANT_SENTINEL,
  getLinkUrl,
  getConnectionStatus,
  unlink,
  type GoogleAdsConnectionStatus,
} from '@/services/googleAdsOAuthService';
import {
  getTenantTier,
  setTenantTier,
} from '@/services/googleAdsKpiDashboardService';
import { canAccessAdmin } from '@/lib/adsToolsAccess';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

const TIER_OPTIONS = [
  { value: 1, label: '1 · Standard' },
  { value: 2, label: '2 · Managed' },
  { value: 3, label: '3 · Premium' },
];

interface Props {
  /** Workspace UUID (string). Required for state-token CSRF binding. */
  workspaceId: string;
  /** Caller's tenant domain (used for the per-tenant *status check*; the
   *  link flow itself uses agency by default since AgileCo's MCC covers
   *  all clients). */
  tenantId: string;
  /** Optional override; defaults to 'admin'. */
  userRole?: string;
  /** When true, skip the outer <Card> wrapper so this can render as a
   *  sub-section of a parent Card. */
  bare?: boolean;
  /** When true, hide all action buttons (Connect / Reconnect / Disconnect)
   *  and surface a hint that the connection is managed elsewhere (Super
   *  Admin → Integrations). Use on the per-workspace integrations tab,
   *  where MCC OAuth is agency-wide and shouldn't be re-bound by every
   *  workspace owner. */
  readOnly?: boolean;
  /**
   * Which OAuth grant this card connects.
   *
   * 'agency' binds the MCC credential that covers EVERY client under the
   * manager account — one grant, agency-wide, and re-binding it silently
   * repoints all of them. That stays restricted to platform admins.
   *
   * 'tenant' binds this one workspace's own Google Ads account. Blast radius
   * is the single workspace, the owner sees breakage immediately, and it is
   * the routine case — so workspace owners and admins can do it themselves.
   *
   * Defaults to 'agency', preserving the existing admin-panel behaviour.
   */
  scope?: 'agency' | 'tenant';
  /**
   * When false, omit the "Client tier" sub-section. Hosts that already
   * render their own management-tier control elsewhere on the same page
   * (e.g. the Settings cascade's Management tier layer) set this to false
   * so the operator doesn't see two tier selectors for the same tenant.
   * Defaults to true, preserving the existing standalone-card behaviour.
   */
  showTierSection?: boolean;
}

export function GoogleAdsOAuthCard({
  workspaceId,
  tenantId,
  userRole = 'admin',
  bare = false,
  readOnly = false,
  scope = 'agency',
  showTierSection = true,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { userProfile } = useWorkspaceStore();
  // The persistent (real) tier setter is platform-admin only. Non-admins see
  // the tier read-only so a client-org member can never self-escalate.
  const canSetTier = canAccessAdmin(userProfile);
  // Same gate as the admin panel itself, so we never offer a button that
  // lands on an "Admin Access Required" wall.
  const canManageAgencyIntegration = canAccessAdmin(userProfile);

  // Persistent product tier — read for everyone, writable only by admins.
  const tierQuery = useQuery({
    queryKey: ['tenant-tier', tenantId],
    queryFn: () => getTenantTier(tenantId),
    enabled: !!tenantId,
  });

  const tierMutation = useMutation({
    mutationFn: (tier: number) => setTenantTier(tenantId, tier),
    onSuccess: (result) => {
      message.success(`Client tier updated to Tier ${result.product_tier}`);
      // Refresh the tier read AND any KPI dashboard (prefix invalidation covers
      // the tenant-scoped + preview-scoped 3-element dashboard keys).
      queryClient.invalidateQueries({ queryKey: ['tenant-tier', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['kpi-dashboard', tenantId] });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? (err instanceof Error ? err.message : String(err));
      message.error(`Could not update tier: ${detail}`);
    },
  });

  const currentTier = tierQuery.data?.product_tier;
  const tierBusy = tierMutation.isPending || tierQuery.isFetching;

  // Surface OAuth callback feedback once, then clear the query params
  // so the same notification doesn't fire again on every render.
  useEffect(() => {
    const ok = searchParams.get('google_ads_connected');
    const err = searchParams.get('google_ads_error');
    if (ok) {
      const level = searchParams.get('level');
      message.success(
        level === 'agency'
          ? 'Google Ads MCC connected — covers all clients under the manager account.'
          : 'Google Ads connected for this tenant.'
      );
      // Force an immediate refetch so the badge flips without waiting for
      // the next polling interval.
      queryClient.invalidateQueries({
        queryKey: ['google-ads-oauth-status', tenantId],
      });
    } else if (err) {
      message.error({
        content: `Google Ads connection failed: ${decodeURIComponent(err)}`,
        duration: 8,
      });
    }
    if (ok || err) {
      const next = new URLSearchParams(searchParams.toString());
      next.delete('google_ads_connected');
      next.delete('google_ads_error');
      next.delete('level');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient, tenantId]);

  // Polled status — refetches every 30s while the page is open so the
  // badge reflects revocations / health-check failures without manual
  // refresh. Per CLAUDE.md polling-endpoint policy, the backend logs this
  // call at DEBUG so it doesn't pollute INFO logs every 30s.
  const { data: status, isLoading: loading } = useQuery<GoogleAdsConnectionStatus>({
    queryKey: ['google-ads-oauth-status', tenantId],
    queryFn: () => getConnectionStatus(tenantId),
    refetchInterval: 30_000,           // poll every 30s
    refetchOnWindowFocus: true,        // refresh when user comes back to tab
    staleTime: 10_000,                 // dedupe rapid mounts
    enabled: !!tenantId,
  });

  const handleConnect = async () => {
    setBusy(true);
    try {
      const url = await getLinkUrl({
        // Agency binds the MCC grant covering every client; tenant binds only
        // this workspace's own account. See the `scope` prop.
        tenantId: scope === 'agency' ? AGENCY_TENANT_SENTINEL : tenantId,
        workspaceId,
        userRole,
      });
      // Hard nav — Google won't accept the redirect via fetch.
      window.location.href = url;
    } catch (e) {
      message.error(
        e instanceof Error ? e.message : "Couldn't start Google authorization"
      );
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Disconnect Google Ads? Per-client mappings will remain but Google Ads writes/reads will fail until reconnected.'
      )
    ) return;
    setBusy(true);
    try {
      // Disconnect at the same level we connected at
      const target =
        status?.level === 'agency' ? AGENCY_TENANT_SENTINEL : tenantId;
      await unlink({ tenantId: target, userRole });
      // Trigger immediate refetch — react-query handles the cache update
      await queryClient.invalidateQueries({
        queryKey: ['google-ads-oauth-status', tenantId],
      });
      message.info('Google Ads link removed.');
    } catch (e) {
      message.error(
        e instanceof Error ? e.message : 'Disconnect failed'
      );
    } finally {
      setBusy(false);
    }
  };

  const isConnected = !!status?.connected;

  const body = (
    <>
      <Typography.Paragraph
        type="secondary"
        style={{ marginBottom: 16, minHeight: 44 }}
      >
        {readOnly
          ? 'Google Ads access is granted at the agency level — your agency admin manages the connection.'
          : 'Authorize once at the agency MCC level — covers all client accounts linked under the manager account.'}
      </Typography.Paragraph>

      {loading ? (
        <Typography.Text type="secondary">Checking connection…</Typography.Text>
      ) : isConnected ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Alert
            type="success"
            showIcon
            message={
              status?.level === 'agency'
                ? 'Connected at agency (MCC) level'
                : 'Connected for this tenant'
            }
            description={
              <>
                {status?.external_account_id && (
                  <div>
                    Account: <code>{status.external_account_id}</code>
                  </div>
                )}
                {status?.last_health_check_at && (
                  <div>
                    Last health check:{' '}
                    {new Date(status.last_health_check_at).toLocaleString()}
                  </div>
                )}
                {status?.last_error && (
                  <div style={{ color: '#cf1322' }}>
                    Last error: {status.last_error}
                  </div>
                )}
              </>
            }
          />
          {!readOnly && (
            <Space>
              <Tooltip title="Re-run the OAuth consent flow (e.g., after revoke)">
                <Button
                  size="small"
                  onClick={handleConnect}
                  disabled={busy}
                  icon={<RefreshCw size={14} />}
                >
                  Reconnect
                </Button>
              </Tooltip>
              <Button
                size="small"
                danger
                onClick={handleDisconnect}
                disabled={busy}
                icon={<XCircle size={14} />}
              >
                Disconnect
              </Button>
            </Space>
          )}
        </div>
      ) : readOnly ? (
        // Dead end, fixed. This used to say "Ask your agency admin to connect
        // Google Ads from Super Admin > Integrations" to EVERYONE — including
        // the admins who could do it themselves, who then went looking for a
        // page that is only reachable from the avatar menu and is invisible
        // unless platform_role is super_admin or platform_owner.
        //
        // The readOnly guard itself is right: agency OAuth is agency-wide, and
        // a workspace owner should not be able to re-bind every client's
        // connection from their own settings page. But "you cannot do this
        // here" has to say where it CAN be done, to the people who can.
        <Alert
          type="warning"
          showIcon
          message="Not connected"
          description={
            canManageAgencyIntegration ? (
              <Space direction="vertical" size={8}>
                <Typography.Text>
                  Google Ads connects once at the agency level and covers every
                  client under the manager account.
                </Typography.Text>
                <Button
                  type="primary"
                  size="small"
                  icon={<LinkIcon size={14} />}
                  onClick={() => navigate(COPY.adminPanel.integrationsPath)}
                >
                  Connect Google Ads
                </Button>
              </Space>
            ) : (
              <Typography.Text>
                Google Ads connects once at the agency level. Ask an agency
                admin to connect it — they will find it under{' '}
                Integrations in the {COPY.adminPanel.label}.
              </Typography.Text>
            )
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Click below to sign in with the Google account that has Admin
            access to the agency MCC. You'll authorize this app to read +
            write Google Ads for all linked client accounts.
          </Typography.Paragraph>
          <Button
            type="primary"
            onClick={handleConnect}
            disabled={busy}
            icon={<LinkIcon size={14} />}
          >
            {busy ? 'Redirecting…' : 'Connect Google Ads'}
          </Button>
        </div>
      )}

      {/* Persistent client tier — gates the KPI dashboard's Tier-2 metrics.
          Admin-only setter (canAccessAdmin); everyone else sees it read-only. */}
      {showTierSection ? (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Typography.Text strong>Client tier</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Controls which KPI dashboard metrics this client can see. Tier 2
              (Managed) unlocks impression share &amp; wasted-spend metrics.
            </Typography.Paragraph>
            {tierQuery.isLoading ? (
              <Spin size="small" />
            ) : canSetTier ? (
              <Space>
                <Select
                  style={{ width: 200 }}
                  value={currentTier}
                  options={TIER_OPTIONS}
                  disabled={tierBusy || tierQuery.isError}
                  onChange={(tier) => tierMutation.mutate(tier)}
                  placeholder="Select tier"
                />
                {tierBusy ? <Spin size="small" /> : null}
              </Space>
            ) : (
              <Tag color={(currentTier ?? 1) >= 2 ? 'success' : 'default'}>
                {currentTier != null
                  ? TIER_OPTIONS.find((o) => o.value === currentTier)?.label ??
                    `Tier ${currentTier}`
                  : 'Tier —'}
              </Tag>
            )}
          </div>
        </>
      ) : null}
    </>
  );

  if (bare) {
    return body;
  }

  return (
    <Card
      title={
        <Space>
          {isConnected ? (
            <CheckCircle size={16} className="text-teal" />
          ) : (
            <AlertTriangle size={16} className="text-coral" />
          )}
          Google Ads
        </Space>
      }
      extra={
        <Tag color={isConnected ? 'success' : 'default'}>
          {isConnected ? 'Connected' : 'Not connected'}
        </Tag>
      }
      style={{ height: '100%' }}
    >
      {body}
    </Card>
  );
}
